import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { Loader2, FolderOpen, Camera, LayoutGrid, List } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { DiaryLayout } from './DiaryLayout';
import { TodoOverview } from './TodoOverview';
import { PAPER, projectColour } from './theme';
import { MarkCompleteButton, CompletedShelf, isPendingComplete } from './ProjectComplete';

const COMPACT_KEY = 'lux_diary_compact';

/**
 * ProjectPicker — Site Diary landing (SD-1 §2).
 *
 * All Live projects the user is assigned to render as a scrollable tile
 * grid — no "View all" toggle, no pagination. Tiles are compact enough
 * that ~6 fit above the fold on a phone before the native scroll kicks in.
 *
 * Ordering (SD-1 §2):
 *   Tile 1  = the project this user has most recently used in the diary
 *             (backend already scopes `last_diary_activity` to this user).
 *   Tile 2+ = remaining projects by `project_ref` newest-first.
 * A brand-new user with no history sees pure job-number order.
 */
export const ProjectPicker = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // DIA-1 §2.6 — preserve the URL prefix when jumping into a project so
  // /portal/diary → /portal/diary/{id} (routes the axios interceptor to
  // send X-View-Context: portal) and /diary → /diary/{id} (dashboard).
  const diaryPrefix = location.pathname.startsWith('/portal/diary') ? '/portal/diary' : '/diary';
  // Dashboard shell uses /captures (plural); /capture redirects and would drop
  // our query. Portal shell uses /portal/capture.
  const capturePath = diaryPrefix.startsWith('/portal') ? '/portal/capture' : '/captures';
  // "+ New → New diary entry" arrives as /diary?new=note — carry it into the chosen project.
  const newParam = new URLSearchParams(location.search).get('new');
  const openProject = (id) => navigate(`${diaryPrefix}/${id}${newParam ? `?new=${encodeURIComponent(newParam)}` : ''}`);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [compact, setCompact] = useState(() => { try { return localStorage.getItem(COMPACT_KEY) === '1'; } catch { return false; } });
  const toggleCompact = () => { const v = !compact; setCompact(v); try { localStorage.setItem(COMPACT_KEY, v ? '1' : '0'); } catch { /* ignore */ } };

  const fetchProjects = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.get('/diary/projects');
      setProjects(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);
  // Office changes (status, merges) show up without a reload — refresh when the tab comes back into view.
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible') fetchProjects(true); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [fetchProjects]);

  // Sort order per SD-1 §2. Locale-aware numeric compare on `project_ref`
  // keeps LUX219 above LUX218 (i.e. newer number wins).
  const sortedProjects = useMemo(() => {
    const withActivity = [];
    const withoutActivity = [];
    for (const p of projects) {
      if (isPendingComplete(p)) continue; // lives on the "Marked complete" shelf
      if (p.last_diary_activity) withActivity.push(p);
      else withoutActivity.push(p);
    }
    withActivity.sort((a, b) => (b.last_diary_activity || '').localeCompare(a.last_diary_activity || ''));

    const byRefDesc = (a, b) =>
      (b.project_ref || '').localeCompare(a.project_ref || '', undefined, { numeric: true, sensitivity: 'base' });

    // Only the SINGLE most-recent gets top billing (per spec: "Tile 1");
    // any other activity ties get sorted back into the job-number list.
    const [mostRecent, ...otherActivity] = withActivity;
    const remainder = [...otherActivity, ...withoutActivity].sort(byRefDesc);
    return mostRecent ? [mostRecent, ...remainder] : remainder;
  }, [projects]);

  const mostRecentId = sortedProjects[0]?.last_diary_activity ? sortedProjects[0].id : null;
  const pendingProjects = useMemo(() => projects.filter(isPendingComplete), [projects]);
  const refresh = () => fetchProjects(true);

  const activityLabel = (project) => {
    const iso = project.last_diary_activity;
    if (!iso) return null;
    try { return `you · ${formatDistanceToNow(parseISO(iso), { addSuffix: true })}`; }
    catch { return null; }
  };

  const renderTile = (project) => {
    const colour = projectColour(project);
    const subtitle = [project.town_city, project.primary_project_type].filter(Boolean).join(' · ');
    const activity = activityLabel(project);
    const isMostRecent = project.id === mostRecentId;
    if (compact) {
      return (
        <div key={project.id} role="button" tabIndex={0} onClick={() => openProject(project.id)} onKeyDown={(e) => { if (e.key === 'Enter') openProject(project.id); }}
          className="flex items-center gap-2.5 px-3 py-2 cursor-pointer active:bg-black/5" data-testid={`diary-project-row-${project.id}`}>
          <span className="w-1.5 self-stretch rounded-full shrink-0" style={{ background: colour.hex }} />
          <span className="flex-1 min-w-0">
            <span className="block text-[13px] font-semibold truncate" style={{ color: PAPER.ink }}>{project.project_ref}{isMostRecent && <span className="ml-1.5 text-[9px] px-1 py-px rounded-full font-bold uppercase" style={{ background: PAPER.accent, color: PAPER.accentInk }}>Last</span>}</span>
            <span className="block text-[10px] truncate" style={{ color: PAPER.muted }}>{[subtitle, activity].filter(Boolean).join(' · ') || 'no entries yet'}</span>
          </span>
          <MarkCompleteButton api={api} project={project} onChanged={refresh} compact />
        </div>
      );
    }
    return (
      <div
        key={project.id}
        role="button"
        tabIndex={0}
        onClick={() => openProject(project.id)}
        onKeyDown={(e) => { if (e.key === 'Enter') openProject(project.id); }}
        className="group relative text-left rounded-xl px-3 py-3 transition-all active:scale-[0.98] flex flex-col overflow-hidden hover:shadow-md cursor-pointer"
        style={{
          background: PAPER.card,
          border: `1px solid ${PAPER.border}`,
          borderLeft: `5px solid ${colour.hex}`,
          boxShadow: PAPER.shadow,
          minHeight: '100px',
        }}
        data-testid={`diary-project-tile-${project.id}`}
      >
        {isMostRecent && (
          <span
            className="absolute top-1.5 right-1.5 text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider"
            style={{ background: PAPER.accent, color: PAPER.accentInk }}
            data-testid="diary-most-recent-badge"
          >
            Last
          </span>
        )}
        <div className="text-[14px] font-semibold line-clamp-2 leading-tight pr-8" style={{ color: PAPER.ink }}>
          {project.project_ref}
        </div>
        {subtitle && (
          <div className="text-[11px] truncate mt-0.5" style={{ color: PAPER.inkSoft }}>{subtitle}</div>
        )}
        <div className="mt-auto pt-1.5 flex items-end justify-between gap-2">
          <span className="text-[10px]" style={{ color: PAPER.muted }}>{activity || 'no entries yet'}</span>
          <MarkCompleteButton api={api} project={project} onChanged={refresh} />
        </div>
      </div>
    );
  };

  return (
    <DiaryLayout>
      <div className="max-w-3xl mx-auto px-4 pt-5 pb-8">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-[26px] font-bold" style={{ color: PAPER.ink }}>Site Diary</h1>
            <p className="text-[14px] mt-1 mb-4" style={{ color: PAPER.inkSoft }}>Choose a project to log</p>
          </div>
          <button type="button" onClick={toggleCompact} className="mt-1.5 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold active:scale-95 transition-transform"
            style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, color: PAPER.inkSoft }} title={compact ? 'Show as tiles' : 'Condense to a list'} data-testid="diary-compact-toggle">
            {compact ? <LayoutGrid className="w-3.5 h-3.5" /> : <List className="w-3.5 h-3.5" />} {compact ? 'Tiles' : 'Condense'}
          </button>
        </div>

        {/* CI-2 Feature E — start a cost capture without opening a job. Leads with
            Fuel / overhead (straight to Xero, no project); materials is one tap away
            and then asks for the job. Same orange-bordered identity as the portal
            Capture hero. */}
        {user?.capture_enabled && (
          <button
            type="button"
            onClick={() => navigate(`${capturePath}?type=general&entry=diary`)}
            className="w-full mb-5 rounded-xl px-4 py-3.5 flex items-center gap-3 transition-all active:scale-[0.99] hover:shadow-md"
            style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, borderLeft: '5px solid #F5A623', boxShadow: PAPER.shadow }}
            data-testid="diary-cost-capture-btn"
          >
            <span className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(245,166,35,0.15)' }}>
              <Camera className="w-5 h-5" style={{ color: '#F5A623' }} />
            </span>
            <span className="text-left min-w-0">
              <span className="block text-[15px] font-bold" style={{ color: PAPER.ink }}>Cost Capture</span>
              <span className="block text-[12px] truncate" style={{ color: PAPER.inkSoft }}>Fuel, overhead or materials — no need to open a job first</span>
            </span>
          </button>
        )}

        {/* SD-TODO — outstanding to-dos across all assigned projects. */}
        {!loading && projects.length > 0 && <TodoOverview projects={sortedProjects} />}

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: PAPER.accent }} />
          </div>
        ) : sortedProjects.length === 0 && pendingProjects.length === 0 ? (
          <div
            className="text-center py-16 rounded-xl"
            style={{ background: PAPER.card, border: `1px solid ${PAPER.border}` }}
            data-testid="diary-empty-state"
          >
            <FolderOpen className="w-8 h-8 mx-auto mb-3" style={{ color: PAPER.hint }} />
            <p className="text-[14px] font-medium" style={{ color: PAPER.ink }}>No projects assigned to you yet.</p>
            <p className="text-[12px] mt-1" style={{ color: PAPER.muted }}>Contact your administrator.</p>
          </div>
        ) : compact ? (
          <div className="rounded-xl overflow-hidden divide-y" style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, boxShadow: PAPER.shadow, borderColor: PAPER.border }} data-testid="diary-project-list">
            {sortedProjects.map(renderTile)}
            {sortedProjects.length === 0 && <div className="px-3 py-4 text-[12px]" style={{ color: PAPER.muted }}>All your jobs are marked complete.</div>}
          </div>
        ) : (
          <div
            className="grid grid-cols-2 gap-2.5"
            data-testid="diary-project-grid"
          >
            {sortedProjects.map(renderTile)}
          </div>
        )}
        {!loading && <CompletedShelf api={api} projects={pendingProjects} onOpen={(p) => openProject(p.id)} onChanged={refresh} />}
      </div>
    </DiaryLayout>
  );
};

export default ProjectPicker;
